import { Button } from "@/components/ui/button"
import { searchUsers } from "@/features/profile/services/user.service"
import { useTranslation } from "@/language"
import { useCommentMutations } from "../hooks/useCommentMutations"
import { useComments } from "../hooks/useComments"
import type { CommentableEntity } from "../services/comment.api"
import { CommentForm } from "./CommentForm"
import { CommentItem } from "./CommentItem"

interface CommentListProps {
  entityType: CommentableEntity
  entityId: string
  canComment: boolean
  canReply?: boolean
  canModerate: boolean
  canNotifyMembers?: boolean
  searchQuery?: string
  headingClassName?: string
  isGuest?: boolean
}

export function CommentList({
  entityType,
  entityId,
  canComment,
  canReply = canComment,
  canModerate,
  canNotifyMembers = false,
  searchQuery,
  headingClassName,
  isGuest = false,
}: CommentListProps) {
  const { t } = useTranslation("rituals")
  const { comments, total, hasNextPage, fetchNextPage, isFetchingNextPage, isLoading, isError } =
    useComments({
      entityType,
      entityId,
      search: searchQuery,
    })
  const mutations = useCommentMutations({ entityType, entityId })

  if (isLoading) {
    return <p className="text-sm text-stone">Loading discussion...</p>
  }

  if (isError) {
    return (
      <div className="rounded-xl border border-red/20 bg-red/5 px-4 py-3 text-sm text-red">
        Failed to load comments. Please try again.
      </div>
    )
  }

  return (
    <div className="pt-2.5 md:pt-5">
      {!headingClassName && (
        <div className="flex items-center justify-between mb-5 md:mb-6">
          <div className="flex items-center gap-1.25 text-center">
            <span
              className={`text-xl font-semibold text-primary uppercase ${headingClassName ?? ""}`}
            >
              {t("discussion.title")}
            </span>
            <span className="text-[#F2F0EB66] text-[18px] leading-5.5">
              {total > 0 ? ` ${total}` : ""}
            </span>
          </div>
        </div>
      )}

      {!canComment && !isGuest && (
        <div
          className="flex items-center gap-3.75 rounded-[10px] px-5 py-[15px]"
          style={{
            border: "1px solid var(--color-discussion-readonly-border)",
            background: "var(--color-discussion-readonly-bg)",
          }}
        >
          <div>
            <p className="text-[14px] font-monteserat font-medium text-primary">
              {t("discussion.viewOnly")}
            </p>
            <p className="text-[13px] text-primary/70 mt-2">
              {t("discussion.viewOnlyDescription")}
            </p>
          </div>
        </div>
      )}

      {canComment && !isGuest && (
        <CommentForm
          placeholder={t("discussion.placeholder")}
          disabled={mutations.isCreating}
          showNotifyMembers={entityType === "ritual" && canNotifyMembers}
          notifyMembersLabel={t("discussion.notifyMembers")}
          onSubmit={async (content, options) => {
            await mutations.createComment({ content, notifyMembers: options?.notifyMembers })
          }}
          onSearchUsers={async query => {
            const result = await searchUsers({ search: query, limit: 10 })
            return result.users.map(u => ({
              id: u.id,
              name: u.name || "",
              username: u.username || "",
              profilePictureUrl: u.profilePictureUrl,
            }))
          }}
        />
      )}

      {comments.length === 0 && canComment ? (
        <div className="text-sm leading-6.25 text-primary mt-5 md:mt-7.5">
          {searchQuery ? t("discussion.noMatch") : t("discussion.empty")}
        </div>
      ) : (
        <div className="space-y-7.5 mt-7.5">
          {comments.map(comment => (
            <CommentItem
              key={comment.id}
              comment={comment}
              canReply={canReply}
              canModerate={canModerate}
              onReply={async (commentId, content) => {
                await mutations.createComment({ content, parentId: commentId })
              }}
              onEdit={async (commentId, content) => {
                await mutations.updateComment({ commentId, params: { content } })
              }}
              onDelete={async commentId => {
                await mutations.deleteComment(commentId)
              }}
              onTogglePin={async commentId => {
                await mutations.togglePinComment(commentId)
              }}
            />
          ))}
        </div>
      )}

      {hasNextPage && (
        <Button
          variant="outline"
          className="w-full"
          onClick={() => void fetchNextPage()}
          disabled={isFetchingNextPage}
        >
          {isFetchingNextPage ? "Loading..." : "Load more"}
        </Button>
      )}
    </div>
  )
}
